import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, map, startWith, switchMap } from 'rxjs/operators';
import { USER } from 'src/app/core/user.service';
import {
  DateRange,
  DateRangeChangeEvent,
} from 'src/app/shared/components/date-range-picker/date-range-picker.model';
import { StatsDto } from 'src/app/shared/flight-log-api/stats/stats-client.model';
import { StatsHttpClient } from 'src/app/shared/flight-log-api/stats/stats-client.service';
import { UserViewModel } from 'src/view-models/user-view-model';

@Component({
  selector: 'fl-airman-flight-duty-report-page',
  templateUrl: './airman-flight-duty-report-page.component.html',
  styleUrls: ['./airman-flight-duty-report-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AirmanFlightDutyReportPageComponent implements OnInit {
  public startDateParam$: Observable<Date | undefined>;
  public endDateParam$: Observable<Date | undefined>;
  public dateRangeParam$: Observable<string>;
  public componentDestruction$ = new Subject();
  public dateRangePicker$: BehaviorSubject<DateRange | undefined> = new BehaviorSubject(
    undefined as DateRange | undefined,
  );
  public stats$: Observable<StatsDto | null>;
  public queryParams$: Observable<any>;

  constructor(
    @Inject(USER) private user: UserViewModel,
    private route: ActivatedRoute,
    private router: Router,
    private statsClient: StatsHttpClient,
  ) {}

  public ngOnInit() {
    this.startDateParam$ = this.route.queryParams.pipe(map((p) => p.startDate));
    this.endDateParam$ = this.route.queryParams.pipe(map((p) => p.endDate));
    this.dateRangeParam$ = this.route.queryParams.pipe(
      map((p) => p.dateRange),
      // we filter out any initial undefined/null values so our startWith is the last value passed
      // to the date-range-picker component as the first value before a new date range is selectd
      filter((dateRange) => dateRange),
      startWith('MonthToDate'),
    );

    this.stats$ = this.dateRangePicker$.pipe(
      filter((s) => s != null),
      map((s) => s as DateRange),
      switchMap((range: DateRange) => {
        return this.statsClient
          .getFlightAndDutyTime(this.user.id, range.startDate, range.endDate)
          .pipe(
            // Start with null so we get a loading indicator
            // will re-activate on date range changes
            startWith(null),
          );
      }),
    );
  }

  public updateDateRange(event: DateRangeChangeEvent) {
    this.dateRangePicker$.next(event.range);
  }

  public updateDateRangeAndRoute(event: DateRangeChangeEvent) {
    this.updateDateRange(event);

    const queryParams: any = {};
    if (event.rangeKey != null) {
      queryParams.dateRange = event.rangeKey;
    } else if (event.range != null) {
      if (event.range.startDate != null) {
        queryParams.startDate = event.range.startDate.toISOString();
      }
      if (event.range.endDate != null) {
        queryParams.endDate = event.range.endDate.toISOString();
      }
    }

    return this.router.navigate([], {
      queryParams,
      relativeTo: this.route,
    });
  }
}
